import { Button, Grid, Slide, Typography } from '@mui/material';
import userPinLogo from 'assets/Images/Auth/pin-screen.png';
import EnterOtpInput from 'components/Common/EnterOtpInput';
import InactiveCommonDialog from 'components/CommonDialog/InactiveCommonDialog';
import { MyContext } from 'Context';
import { getLocalStorageItem } from 'hooks';
import {
  useAuthMe,
  useLogOutApiQuery,
  useSetIdelTimeQuery,
  useVerifyPinQuery,
} from 'queries/AuthQueries';
import React, { useContext, useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { routes } from 'routes/RouteConstants';

import { queryKeys } from '../../constants';

interface InactivityTrackerProps {
  inactivityTime: number;
}

const InactivityTracker: React.FC<InactivityTrackerProps> = () => {
  const userDetails = getLocalStorageItem('roles');
  const [showPinModal, setShowPinModal] = useState(false);
  const [logout, setLogout] = useState(false);
  const [otp, setOtp] = useState<String>('');
  const [otpError, setOtpError] = useState<string>('');
  // navigate
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // query
  const { mutate: setIdleTime } = useSetIdelTimeQuery();
  const { mutate: verifyPin } = useVerifyPinQuery();
  const { mutate: logoutApi } = useLogOutApiQuery();
  const { data: profileData } = useAuthMe();

  useEffect(() => {
    setOtpError('');
  }, [showPinModal]);
  useEffect(() => {
    let inactivityTimer: NodeJS.Timeout | null = null;
    let pinModalOpened = false; // Flag to track whether the pin modal has been opened
    let fullLogout: any = null;
    let pinTimeout: any = null;

    if (profileData?.data) {
      setShowPinModal(profileData?.data?.isIdle);
      fullLogout = profileData?.data?.account?.doLogoutAfter; // You can change this value as needed
      pinTimeout = profileData?.data?.account?.pinRequiredAfter; // You can change this value as needed
    }

    const resetInactivityTimer = () => {
      if (inactivityTimer) {
        clearTimeout(inactivityTimer);
      }

      if (
        (fullLogout !== null && typeof fullLogout !== 'undefined') ||
        (pinTimeout !== null && typeof pinTimeout !== 'undefined')
      ) {
        inactivityTimer = setTimeout(() => {
          if (!logout) {
            if (pinTimeout !== null && typeof pinTimeout !== 'undefined') {
              setShowPinModal(true);
              // Check if pin modal has been opened before executing setIdleTime logic
              if (!pinModalOpened) {
                setIdleTime(
                  {
                    status: true,
                  },
                  {
                    onSuccess: () => {
                      // Additional onSuccess logic for pin timeout
                      pinModalOpened = true; // Set the flag to true after the logic has been executed
                    },
                    onError: () => {},
                  }
                );
              }
            }
            if (fullLogout !== null && typeof fullLogout !== 'undefined') {
              const fullLogoutTimer = setTimeout(() => {
                setLogout(true);
                setShowPinModal(false);
                logoutApi(null, {
                  onSuccess: () => {
                    setIdleTime(
                      {
                        status: false,
                      },
                      {
                        onSuccess: () => {
                          setShowPinModal(false);
                          localStorage.clear();
                          navigate(routes.LOGIN.pathname);
                        },
                        onError: () => {},
                      }
                    );
                  },
                  onError: (err: any) => {
                    setOtpError(err?.fieldErrors?.[0]?.message);
                  },
                });
              }, (fullLogout ?? 0) * 60 * 1000);
              inactivityTimer = fullLogoutTimer;
            }
          }
        }, (pinTimeout ?? 0) * 60 * 1000); // Initial inactivity timeout
      }
    };

    const handleActivity = () => {
      resetInactivityTimer();
    };

    document.addEventListener('click', handleActivity);
    document.addEventListener('keydown', handleActivity);
    document.addEventListener('mousemove', handleActivity);
    document.addEventListener('touchstart', handleActivity);

    resetInactivityTimer();

    return () => {
      if (inactivityTimer) {
        clearTimeout(inactivityTimer);
      }
      document.removeEventListener('click', handleActivity);
      document.removeEventListener('keydown', handleActivity);
      document.removeEventListener('mousemove', handleActivity);
      document.addEventListener('touchstart', handleActivity);
    };
  }, [logout, profileData?.data]);

  const handlePinSubmit = () => {
    verifyPin(
      {
        passcode: otp,
        onboardingMode: null,
      },
      {
        onSuccess: () => {
          setIdleTime(
            {
              status: false,
            },
            {
              onSuccess: () => {
                setShowPinModal(false);
                setOtp('');
                queryClient.invalidateQueries([queryKeys.AUTH_ME]);
              },
              onError: () => {},
            }
          );
        },
        onError: (err: any) => {
          setOtpError(err?.message);
        },
      }
    );
  };
  useEffect(() => {
    if (otp?.length === 4) {
      handlePinSubmit();
    }
  }, [otp]);

  const { logout: chatLogout } = useContext(MyContext);

  // hanlders logout
  const handleLogout = () => {
    logoutApi(null, {
      onSuccess: () => {
        setIdleTime(
          {
            status: false,
          },
          {
            onSuccess: () => {
              chatLogout();
              setShowPinModal(false);
              localStorage.clear();
              navigate(routes.LOGIN.pathname);
              window.location.reload();
            },
            onError: () => {},
          }
        );
      },
      onError: (err: any) => {
        setOtpError(err?.fieldErrors?.[0]?.message);
      },
    });
  };

  return (
    <div>
      <InactiveCommonDialog
        maxWidth="sm"
        open={showPinModal}
        isCrossIcon={false}
      >
        <Slide
          direction={'right'}
          in
          timeout={600}
          easing={{
            enter: 'cubic-bezier(0, 1.5, .8, 1)',
            exit: 'linear',
          }}
        >
          <div className="mx-auto  rounded-[12px] ">
            <div className="mx-auto mb-4 text-center">
              <img src={userPinLogo} alt="pin logo" className="mb-3" />
              <Typography
                fontWeight={600}
                variant="h5"
                color="secondary.100"
                align="center"
              >
                {' '}
                Hi, {userDetails?.name}{' '}
              </Typography>

              <Typography
                fontWeight={400}
                variant="subtitle1"
                color="font.900"
                paddingTop={1.2}
              >
                User ID :{' '}
                <span className="font-bold text-secondary-100">
                  {userDetails?.username}
                </span>
                <span
                  className="ml-[6px] cursor-pointer font-semibold text-primary-600"
                  onClick={handleLogout}
                >
                  {' '}
                  Logout
                </span>
              </Typography>
            </div>

            <form id="pin-form">
              <Grid item container spacing={2} xs={12}>
                <Grid item xs={12}>
                  <EnterOtpInput
                    value={otp}
                    otpDigit={4}
                    onChange={(userOTP: string) => {
                      setOtp(userOTP);
                    }}
                  />
                  {otpError && (
                    <p className="mt-1 text-center text-sm text-error-500">
                      {otpError}
                    </p>
                  )}
                </Grid>

                <Typography
                  fontWeight={400}
                  variant="subtitle1"
                  color="font.900"
                  textAlign={'center'}
                  margin={'auto'}
                  paddingBottom={2}
                >
                  Please enter your PIN.
                </Typography>
                <Grid item xs={12}>
                  <Button
                    id="pin-form"
                    onClick={handlePinSubmit}
                    fullWidth
                    disabled={otp?.length < 4}
                  >
                    Confirm{' '}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </div>
        </Slide>
      </InactiveCommonDialog>
    </div>
  );
};

export default InactivityTracker;
